class CustomerReviewsSlider extends HTMLElement {
  constructor() {
    super();
    this.handleFocusIn = this.pause.bind(this);
    this.handleFocusOut = this.resume.bind(this);
    this.handleVisibilityChange = this.onVisibilityChange.bind(this);
    this.handleThemeEditorBlockSelect = this.onThemeEditorBlockSelect.bind(this);
    this.handleThemeEditorBlockDeselect = this.onThemeEditorBlockDeselect.bind(this);
    this.isInitialized = false;
    this.themeEditorListenersAttached = false;
    this.pendingInitFrame = null;
  }

  connectedCallback() {
    this.isThemeEditor = this.isShopifyDesignMode();
    this.autoplayEnabled = this.dataset.autoplay === 'true' && !this.isThemeEditor;
    this.autoplaySpeed = this.getAutoplaySpeed();
    this.initializeSlider();
  }

  initializeSlider() {
    this.sliderComponent = this.querySelector('slider-component');
    this.slider = this.querySelector('[id^="Slider-"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.prevButton = this.querySelector('button[name="previous"]');

    if (!this.sliderComponent || !this.slider) {
      this.scheduleInitRetry();
      return;
    }

    this.cancelInitRetry();
    if (this.isInitialized) return;

    this.pause();
    this.addEventListener('focusin', this.handleFocusIn);
    this.addEventListener('focusout', this.handleFocusOut);
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }
    this.attachThemeEditorListeners();
    this.isInitialized = true;

    if (this.autoplayEnabled) {
      this.resume();
    }
  }

  disconnectedCallback() {
    this.cancelInitRetry();
    this.pause();
    this.removeEventListener('focusin', this.handleFocusIn);
    this.removeEventListener('focusout', this.handleFocusOut);
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }
    this.detachThemeEditorListeners();
    this.isInitialized = false;
  }

  resume() {
    if (!this.autoplayEnabled) return;
    if (typeof document !== 'undefined' && document.hidden) return;
    this.pause();
    this.autoplay = window.setInterval(() => this.autoRotateSlides(), this.autoplaySpeed);
  }

  pause() {
    window.clearInterval(this.autoplay);
    this.autoplay = null;
  }

  autoRotateSlides() {
    if (!this.sliderComponent || !this.slider) return;
    const measurements = this.getSliderMeasurements();
    if (!measurements || measurements.totalPages < 2) return;

    const nextPosition =
      measurements.currentPage >= measurements.totalPages
        ? 0
        : this.slider.scrollLeft + measurements.sliderItemOffset;

    this.sliderComponent.setSlidePosition(nextPosition);
  }

  getAutoplaySpeed() {
    const parsedSpeed = Number(this.dataset.speed || 2);
    return (Number.isFinite(parsedSpeed) && parsedSpeed > 0 ? parsedSpeed : 2) * 1000;
  }

  onVisibilityChange() {
    if (typeof document === 'undefined') return;
    if (document.hidden) {
      this.pause();
      return;
    }

    this.resume();
  }

  isShopifyDesignMode() {
    return Boolean(
      typeof window !== 'undefined' && window.Shopify && window.Shopify.designMode
    );
  }

  attachThemeEditorListeners() {
    if (!this.isThemeEditor || this.themeEditorListenersAttached || typeof document === 'undefined') return;

    document.addEventListener('shopify:block:select', this.handleThemeEditorBlockSelect);
    document.addEventListener('shopify:block:deselect', this.handleThemeEditorBlockDeselect);
    this.themeEditorListenersAttached = true;
  }

  detachThemeEditorListeners() {
    if (!this.themeEditorListenersAttached || typeof document === 'undefined') return;

    document.removeEventListener('shopify:block:select', this.handleThemeEditorBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.handleThemeEditorBlockDeselect);
    this.themeEditorListenersAttached = false;
  }

  onThemeEditorBlockSelect(event) {
    const slide = this.getThemeEditorSlide(event.target);
    if (!slide) return;

    this.pause();
    this.clearThemeEditorActiveSlide();
    slide.classList.add('customer-reviews__item--editor-active');
    this.scrollToSlide(slide);
    this.focusSlide(slide);
  }

  onThemeEditorBlockDeselect(event) {
    const slide = this.getThemeEditorSlide(event.target);
    if (!slide) return;

    slide.classList.remove('customer-reviews__item--editor-active');
  }

  getThemeEditorSlide(target) {
    if (!target || typeof target.closest !== 'function') return null;

    const slide = target.closest('[id^="Slide-"]');
    if (!slide || !this.contains(slide)) return null;

    return slide;
  }

  clearThemeEditorActiveSlide() {
    this.querySelectorAll('.customer-reviews__item--editor-active').forEach((slide) => {
      slide.classList.remove('customer-reviews__item--editor-active');
    });
  }

  scrollToSlide(slide) {
    const position = slide.offsetLeft || 0;

    if (this.sliderComponent && typeof this.sliderComponent.setSlidePosition === 'function') {
      this.sliderComponent.setSlidePosition(position);
      return;
    }

    if (this.slider && typeof this.slider.scrollTo === 'function') {
      this.slider.scrollTo({ left: position });
      return;
    }

    if (this.slider) {
      this.slider.scrollLeft = position;
    }
  }

  focusSlide(slide) {
    if (typeof slide.setAttribute === 'function') {
      slide.setAttribute('tabindex', '-1');
    }

    if (typeof slide.focus === 'function') {
      slide.focus({ preventScroll: true });
    }
  }

  scheduleInitRetry() {
    if (this.pendingInitFrame || typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
      return;
    }

    this.pendingInitFrame = window.requestAnimationFrame(() => {
      this.pendingInitFrame = null;
      if (!this.isConnected) return;
      this.initializeSlider();
    });
  }

  cancelInitRetry() {
    if (!this.pendingInitFrame || typeof window === 'undefined' || typeof window.cancelAnimationFrame !== 'function') {
      this.pendingInitFrame = null;
      return;
    }

    window.cancelAnimationFrame(this.pendingInitFrame);
    this.pendingInitFrame = null;
  }

  getSliderMeasurements() {
    return this.getComponentMeasurements() || this.getFallbackMeasurements();
  }

  getComponentMeasurements() {
    if (!this.sliderComponent) return null;
    if (!this.sliderComponent.sliderItemOffset || !this.sliderComponent.totalPages) return null;

    return {
      sliderItemOffset: this.sliderComponent.sliderItemOffset,
      totalPages: this.sliderComponent.totalPages,
      currentPage:
        this.sliderComponent.currentPage || Math.round(this.slider.scrollLeft / this.sliderComponent.sliderItemOffset) + 1,
    };
  }

  getFallbackMeasurements() {
    if (!this.slider) return null;

    const slideNodes = Array.from(this.querySelectorAll('[id^="Slide-"]')).filter((slide) => slide.clientWidth > 0);
    if (slideNodes.length < 2) return null;

    const sliderItemOffset = slideNodes[1].offsetLeft - slideNodes[0].offsetLeft;
    if (!sliderItemOffset) return null;

    const firstOffset = slideNodes[0].offsetLeft || 0;
    const slidesPerPage = Math.max(1, Math.floor((this.slider.clientWidth - firstOffset) / sliderItemOffset));
    const totalPages = Math.max(1, slideNodes.length - slidesPerPage + 1);

    return {
      sliderItemOffset,
      totalPages,
      currentPage: Math.round(this.slider.scrollLeft / sliderItemOffset) + 1,
    };
  }
}

if (!customElements.get('customer-reviews-slider')) {
  customElements.define('customer-reviews-slider', CustomerReviewsSlider);
}
